import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import CashCollection from "../CashCollection";
import ModelProxyClass from "./ModelProxyClass";
import Location from "../Location";
import CashAccount from "../CashAccount";
import AmountDetails from "../AmountDetails";
import CashProcessingLineItem from "../CashProcessingLineItem";


/**
 * @class CashCollectionBase
 * @extends ModelBaseClass
 * @property {number} id (int64)
 * @property {number} tenantId (int64)
 * @property {'VgtDrop'|'RtBvDrop'|'RtAudit'} type
 * @property {Location} location
 * @property {number} cashCollectionLinkId (int64)
 * @property {number} currentCashAccountId (int64) the current cashAccount that has this Collection.  If just collected, then this would be the Person who collected it.  If deposited, it would be the vault who has it.  Once processed, this would be null.  If Undone-Deposit and is back with antoher Person, this would be the Other person who has it.
 * @property {CashAccount} fromCashAccount the machinerecord cashaccount that this was collected from
 * @property {CashAccount} collectorCashAccount the person cashaccount that performed the collection
 * @property {CashAccount} toCashAccount the bank or vault cashaccount that this was eventually deposited to (or null if not yet deposited)
 * @property {number} vgtPreviousMeter
 * @property {number} vgtCurrentMeter
 * @property {boolean} vgtRamClearIndicator if emergency RAM cleared, this will be true.  If standard (non-emergency) RAM clear, this will be false.  Otherwise this will be NULL
 * @property {number} uncountedAmount
 * @property {AmountDetails} uncountedDetails
 * @property {CashAccount} reverseDepositFromCashAccount ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set -- the bank or vault cashaccount that this was most recently reverse-deposited from (or null if never reverse deposited).
 * @property {string} reverseDepositNote ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set -- the supplied note during the most recent reverseDeposit
 * @property {string} processorName ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set
 * @property {Date} dateGamingDay (date only)
 * @property {Date} dateDeposited (date and time)
 * @property {Date} dateProcessed (date and time)
 * @property {Date} dateCreated (date and time)
 * @property {CashProcessingLineItem} cashProcessingLineItem only set if this has been processed
 */
class CashCollectionBase extends ModelBaseClass {
	/**
	 * @type {number} id (int64)
	 */
	id;
	/**
	 * @type {number} tenantId (int64)
	 */
	tenantId;
	/**
	 * @type {'VgtDrop'|'RtBvDrop'|'RtAudit'} type
	 */
	type;
	/**
	 * @type {Location} location
	 */
	location;
	/**
	 * @type {number} cashCollectionLinkId (int64)
	 */
	cashCollectionLinkId;
	/**
	 * the current cashAccount that has this Collection.  If just collected, then this would be the Person who collected it.  If deposited, it would be the vault who has it.  Once processed, this would be null.  If Undone-Deposit and is back with antoher Person, this would be the Other person who has it.
	 * @type {number} currentCashAccountId (int64)
	 */
	currentCashAccountId;
	/**
	 * the machinerecord cashaccount that this was collected from
	 * @type {CashAccount} fromCashAccount
	 */
	fromCashAccount;
	/**
	 * the person cashaccount that performed the collection
	 * @type {CashAccount} collectorCashAccount
	 */
	collectorCashAccount;
	/**
	 * the bank or vault cashaccount that this was eventually deposited to (or null if not yet deposited)
	 * @type {CashAccount} toCashAccount
	 */
	toCashAccount;
	/**
	 * @type {number} vgtPreviousMeter
	 */
	vgtPreviousMeter;
	/**
	 * @type {number} vgtCurrentMeter
	 */
	vgtCurrentMeter;
	/**
	 * if emergency RAM cleared, this will be true.  If standard (non-emergency) RAM clear, this will be false.  Otherwise this will be NULL
	 * @type {boolean} vgtRamClearIndicator
	 */
	vgtRamClearIndicator;
	/**
	 * @type {number} uncountedAmount
	 */
	uncountedAmount;
	/**
	 * @type {AmountDetails} uncountedDetails
	 */
	uncountedDetails;
	/**
	 * ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set -- the bank or vault cashaccount that this was most recently reverse-deposited from (or null if never reverse deposited).
	 * @type {CashAccount} reverseDepositFromCashAccount
	 */
	reverseDepositFromCashAccount;
	/**
	 * ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set -- the supplied note during the most recent reverseDeposit
	 * @type {string} reverseDepositNote
	 */
	reverseDepositNote;
	/**
	 * ONLY set on CollectionsApi::collectionSearch() with collectedByPersonId set
	 * @type {string} processorName
	 */
	processorName;
	/**
	 * @type {Date} dateGamingDay (date only)
	 */
	dateGamingDay;
	/**
	 * @type {Date} dateDeposited (date and time)
	 */
	dateDeposited;
	/**
	 * @type {Date} dateProcessed (date and time)
	 */
	dateProcessed;
	/**
	 * @type {Date} dateCreated (date and time)
	 */
	dateCreated;
	/**
	 * only set if this has been processed
	 * @type {CashProcessingLineItem} cashProcessingLineItem
	 */
	cashProcessingLineItem;

	/**
	 * Instantiates a new instance of CashCollection based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {CashCollection}
	 */
	static create(genericObject) {
		const newCashCollection = new CashCollection();
		newCashCollection.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newCashCollection;
	}

	/**
	 * Instantiates a new array of CashCollection based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[CashCollection]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newCashCollectionArray = [];
		genericArray.forEach(genericObject => {
			newCashCollectionArray.push(CashCollection.create(genericObject));
		});
		return newCashCollectionArray;
	}
}

const _modelDefinition = [
	ModelBaseClass.createModelProperty('id', 'integer'),
	ModelBaseClass.createModelProperty('tenantId', 'integer'),
	ModelBaseClass.createModelProperty('type', 'string'),
	ModelBaseClass.createModelProperty('location', 'Location'),
	ModelBaseClass.createModelProperty('cashCollectionLinkId', 'integer'),
	ModelBaseClass.createModelProperty('currentCashAccountId', 'integer'),
	ModelBaseClass.createModelProperty('fromCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('collectorCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('toCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('vgtPreviousMeter', 'float'),
	ModelBaseClass.createModelProperty('vgtCurrentMeter', 'float'),
	ModelBaseClass.createModelProperty('vgtRamClearIndicator', 'boolean'),
	ModelBaseClass.createModelProperty('uncountedAmount', 'float'),
	ModelBaseClass.createModelProperty('uncountedDetails', 'AmountDetails'),
	ModelBaseClass.createModelProperty('reverseDepositFromCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('reverseDepositNote', 'string'),
	ModelBaseClass.createModelProperty('processorName', 'string'),
	ModelBaseClass.createModelProperty('dateGamingDay', 'datetime'),
	ModelBaseClass.createModelProperty('dateDeposited', 'datetime'),
	ModelBaseClass.createModelProperty('dateProcessed', 'datetime'),
	ModelBaseClass.createModelProperty('dateCreated', 'datetime'),
	ModelBaseClass.createModelProperty('cashProcessingLineItem', 'CashProcessingLineItem'),
];

export default CashCollectionBase;
